import { handleApi } from "@/utils/handleApi";
import { axiosInstanceWebsite } from "../instance";

export interface ProductFunnelAngle {
  websiteId: string;
  funnelNumber: number | null;
  funnelName: string;
  angleName: string;
  angleDescription: string;
}

interface RawFunnelSummary {
  id: string | number;
  brand_name?: string;
  id_sequence_owner_id?: number;
  sale_angle?: { name?: string; description?: string } | null;
  product?: { id?: string } | null;
  products?: Array<{ product_id?: string; id?: string }> | null;
  configurations?: { name_funnel?: string } | null;
}

const matchesProduct = (site: RawFunnelSummary, productId: string) => {
  if (site.product?.id === productId) return true;
  return (site.products ?? []).some(
    (p) => p.product_id === productId || p.id === productId
  );
};

// Paginación del summary: igual que la lista de embudos, se pide en páginas
// acotadas y se ensambla el total (evita el request único sin límite que se
// pasaba del timeout en cuentas con muchos embudos).
const FUNNELS_SUMMARY_PAGE_SIZE = 100;
const FUNNELS_SUMMARY_MAX_PAGES = 500;

const fetchAllFunnelSummaries = async (): Promise<RawFunnelSummary[]> => {
  const all: RawFunnelSummary[] = [];
  let offset = 0;
  for (let page = 0; page < FUNNELS_SUMMARY_MAX_PAGES; page += 1) {
    const { data } = await axiosInstanceWebsite.get<RawFunnelSummary[]>(
      "/websites/summary",
      { params: { limit: FUNNELS_SUMMARY_PAGE_SIZE, offset } }
    );
    const rows = Array.isArray(data) ? data : [];
    all.push(...rows);
    if (rows.length < FUNNELS_SUMMARY_PAGE_SIZE) break;
    offset += FUNNELS_SUMMARY_PAGE_SIZE;
  }
  return all;
};

// Reusa GET /websites/summary (ya lo consume la lista de embudos) y devuelve, para
// un producto, los embudos que lo usan con su nº y su ángulo de venta.
export const getProductFunnelAngles = async (
  productId: string
): Promise<ProductFunnelAngle[]> => {
  const { res } = await handleApi<RawFunnelSummary[]>(
    fetchAllFunnelSummaries(),
    false
  );
  const list = Array.isArray(res) ? res : [];
  return list
    .filter((site) => matchesProduct(site, productId))
    .map((site) => ({
      websiteId: String(site.id),
      funnelNumber: site.id_sequence_owner_id ?? null,
      funnelName: site.configurations?.name_funnel?.trim() || site.brand_name || "",
      angleName: site.sale_angle?.name?.trim() || "",
      angleDescription: site.sale_angle?.description?.trim() || "",
    }));
};
